import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { Reveal, MaskLines } from "@/components/Motion";
import { api } from "@/lib/api";
import { firstBook, getBookStage, arcOpenAt } from "@/config/site";

const ARC_CONTACT_EMAIL = "Arcs@DhammaPublishingHouse.com";

const PLATFORMS = ["TikTok / BookTok", "Instagram / Bookstagram", "Facebook", "Goodreads", "Amazon", "YouTube", "Blog / Website", "Other"];
const GENRES = ["Urban Fiction", "Black Fiction", "Crime / Suspense", "Mystery", "Romance", "Family Drama", "Psychological Fiction", "Other"];
const FREQ = ["Multiple times per week", "Weekly", "A few times per month", "Occasionally", "I am new to book content"];
const STYLES = ["Detailed written reviews", "Short-form social reviews", "Video reactions / commentary", "A mix of formats", "I am building my reviewer presence"];
const CONDUCT = [
  "I understand an ARC is for my personal review use only.",
  "I will not forward, upload, reproduce, sell, or redistribute the ARC.",
  "I understand digital ARC copies may contain my name, email, and a unique copy ID.",
  "I understand receiving an ARC does not require a positive review.",
  "I agree to provide an honest review if I choose to post one.",
  "I understand Dhamma Publishing House may decline or revoke ARC access at its discretion.",
];
const BEFORE = [
  "Applicants must be 18 or older.",
  "Reviewers should actively read and/or discuss books online or within a reading community.",
  "Selected reviewers must not share, upload, reproduce, sell, forward, or redistribute ARC files.",
  "Digital ARC files may contain personalized identifying information and a unique copy ID.",
  "Receiving an ARC does not require a positive review.",
  "Honest reviews are welcome.",
  "Major spoilers should not be posted before the approved review window.",
  "Approved applicants will be asked to accept the Dhamma ARC Reviewer Agreement before receiving an ARC.",
];

const inputCls = "w-full border-b border-dh-cream/20 bg-transparent px-1 py-3 font-body text-base text-dh-cream placeholder:text-dh-muted/60 outline-none transition-colors duration-300 focus:border-dh-gold";
const labelCls = "mb-2 block font-body text-[11px] uppercase tracking-[0.2em] text-dh-muted";

function Section({ title, children }) {
  return (
    <div className="mt-14">
      <h2 className="font-display text-2xl text-dh-gold sm:text-3xl">{title}</h2>
      <div className="mt-7 space-y-7">{children}</div>
    </div>
  );
}

function CheckGroup({ options, values, onToggle, testPrefix }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {options.map((o) => (
        <label key={o} data-testid={`${testPrefix}-${o}`} className="flex cursor-pointer items-start gap-3 border border-dh-cream/10 bg-dh-bg2/40 p-3 transition-colors hover:border-dh-gold/40">
          <input type="checkbox" checked={values.includes(o)} onChange={() => onToggle(o)} className="mt-1 h-4 w-4 accent-dh-gold" />
          <span className="font-body text-sm text-dh-cream/85">{o}</span>
        </label>
      ))}
    </div>
  );
}

function Radio({ name, options, value, onChange }) {
  return (
    <div className="flex flex-wrap gap-3">
      {options.map((o) => (
        <label key={o} className={`cursor-pointer border px-4 py-2.5 font-body text-sm transition-colors ${value === o ? "border-dh-gold bg-dh-gold/10 text-dh-gold" : "border-dh-cream/15 text-dh-cream/80 hover:border-dh-cream/40"}`}>
          <input type="radio" name={name} className="sr-only" checked={value === o} onChange={() => onChange(o)} />
          {o}
        </label>
      ))}
    </div>
  );
}

const initial = {
  full_name: "", email: "", preferred_name: "", city: "", state: "", age_confirmation: "",
  social_handles: "", review_platforms: [], profile_links: "", follower_count: "", posting_frequency: "",
  genres: [], recent_books_authors: "", interest_reason: "", reviewing_style: "",
  preferred_arc_format: "", review_window_commitment: "", spoiler_agreement: "", disclosure_agreement: "",
  accessibility_needs: "", prior_arc_experience: "", additional_notes: "",
  conduct_acknowledgments: [], certification_accepted: false,
};

export default function Arc() {
  const [f, setF] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [duplicate, setDuplicate] = useState(false);
  // Applications open Sept 19, 2026 at 10:00 AM PT (site.js: arcOpenAt).
  // Reactive check so a visitor already on the page sees the form unlock at the
  // exact moment without refreshing. (Interval, not a single timeout: a bare
  // setTimeout would overflow its ~24.8-day max before September.)
  const [arcOpen, setArcOpen] = useState(() => Date.now() >= new Date(arcOpenAt).getTime());
  useEffect(() => {
    if (arcOpen) return undefined;
    const id = setInterval(() => {
      if (Date.now() >= new Date(arcOpenAt).getTime()) setArcOpen(true);
    }, 1000);
    return () => clearInterval(id);
  }, [arcOpen]);
  // Title stays hidden until the staged reveal (site.js: stage >= 2 AND title filled).
  const titleRevealed = getBookStage() >= 2 && Boolean(firstBook.title);
  const bookRef = titleRevealed ? firstBook.title : "Dhamma Publishing House\u2019s upcoming debut release";

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const toggle = (k, v) => setF((s) => ({ ...s, [k]: s[k].includes(v) ? s[k].filter((x) => x !== v) : [...s[k], v] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.full_name.trim() || !f.email.trim() || !f.age_confirmation) {
      toast.error("Please complete the required fields (name, email, age).");
      return;
    }
    if (f.age_confirmation === "I am under 18") {
      toast.error("ARC applicants must be 18 years of age or older.");
      return;
    }
    if (f.conduct_acknowledgments.length < CONDUCT.length) {
      toast.error("Please acknowledge all ARC conduct & security items.");
      return;
    }
    if (!f.certification_accepted) {
      toast.error("Please accept the certification to submit.");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/arc/apply", f);
      if (res.data.duplicate) setDuplicate(true);
      else setDone(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something interrupted your application. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!arcOpen) {
    return (
      <section data-testid="arc-closed" className="flex min-h-[80vh] items-center bg-dh-bg px-5 pt-32 pb-24">
        <div className="mx-auto max-w-2xl text-center">
          <Reveal>
            <p className="font-body text-[11px] uppercase tracking-ultra text-dh-gold">The Dhamma ARC Team</p>
          </Reveal>
          <div className="gold-rule mx-auto mt-8 w-24" />
          <h1 className="mt-10 font-display text-4xl leading-tight text-dh-cream sm:text-5xl">
            ARC applications open September 19 at 10:00 AM PT
          </h1>
          <p className="mx-auto mt-6 max-w-lg font-body text-base leading-relaxed text-dh-cream/70 sm:text-lg">
            Read early. Review honestly. Help us leave something behind. We&rsquo;d love to hear from you when applications open.
          </p>
          <p className="mt-8 font-body text-sm text-dh-cream/60">
            Questions in the meantime?{" "}
            <a href={`mailto:${ARC_CONTACT_EMAIL}`} className="text-dh-gold underline-offset-4 transition-colors hover:underline">{ARC_CONTACT_EMAIL}</a>
          </p>
        </div>
      </section>
    );
  }

  if (duplicate) {
    return (
      <section data-testid="arc-duplicate" className="flex min-h-[80vh] items-center bg-dh-bg px-5 pt-32 pb-24">
        <div className="mx-auto max-w-2xl text-center">
          <div className="gold-rule mx-auto mb-10 w-24" />
          <h1 className="font-display text-4xl text-dh-cream sm:text-5xl">Looks like you&rsquo;ve already applied. &#x1F511;</h1>
          <p className="mt-6 font-body text-base leading-relaxed text-dh-cream/75 sm:text-lg">
            We already have an ARC application associated with this email address. Only one application
            is needed per reviewer for this release.
          </p>
          <p className="mt-6 font-body text-sm text-dh-cream/60">
            If you need to update your application or have questions, contact us at{" "}
            <a data-testid="arc-duplicate-contact" href={`mailto:${ARC_CONTACT_EMAIL}`} className="text-dh-gold underline-offset-4 transition-colors hover:underline">{ARC_CONTACT_EMAIL}</a>.
          </p>
          <p className="mt-8 font-serif-soft text-xl italic text-dh-gold">Stories That Leave Something Behind.</p>
        </div>
      </section>
    );
  }

  if (done) {
    return (
      <section data-testid="arc-success" className="flex min-h-[80vh] items-center bg-dh-bg px-5 pt-32 pb-24">
        <div className="mx-auto max-w-2xl text-center">
          <div className="gold-rule mx-auto mb-10 w-24" />
          <h1 className="font-display text-4xl text-dh-cream sm:text-5xl">Application received.</h1>
          <p className="mt-6 font-body text-base leading-relaxed text-dh-cream/75 sm:text-lg">
            Thank you for your interest in joining the Dhamma ARC Team. Submission does not guarantee selection.
            Approved readers will be contacted directly.
          </p>
          <p className="mt-6 font-body text-sm text-dh-cream/60">
            Questions about the ARC program?{" "}
            <a data-testid="arc-contact-email" href={`mailto:${ARC_CONTACT_EMAIL}`} className="text-dh-gold underline-offset-4 transition-colors hover:underline">{ARC_CONTACT_EMAIL}</a>
          </p>
          <p className="mt-8 font-serif-soft text-xl italic text-dh-gold">Stories That Leave Something Behind.</p>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="arc-page" className="bg-dh-bg pt-32 pb-28 sm:pt-40">
      <div className="mx-auto max-w-[880px] px-5 sm:px-8">
        <Reveal>
          <p className="font-body text-[11px] uppercase tracking-ultra text-dh-gold">The Dhamma ARC Team</p>
        </Reveal>
        <h1 className="mt-6 font-display text-4xl leading-[1.08] text-dh-cream sm:text-6xl">
          <MaskLines lines={["Join the Dhamma", "ARC Team"]} />
        </h1>
        <Reveal delay={0.15}>
          <p className="mt-6 font-serif-soft text-xl italic text-dh-cream/80 sm:text-2xl">
            Read early. Review honestly. Help us leave something behind.
          </p>
        </Reveal>
        <Reveal delay={0.25}>
          <p className="mt-8 max-w-2xl font-body text-base leading-relaxed text-dh-cream/75">
            Thank you for your interest in becoming an Advance Reader for Dhamma Publishing House. ARC readers
            receive early access to select titles in exchange for consideration of an honest review. Applying does
            not guarantee selection.
          </p>
        </Reveal>

        <Reveal>
          <div className="mt-12 border border-dh-gold/25 bg-dh-forest/15 p-7 sm:p-9">
            <h2 className="font-display text-2xl text-dh-cream">Before You Apply</h2>
            <ul className="mt-5 space-y-3">
              {BEFORE.map((b) => (
                <li key={b} className="flex gap-3 font-body text-sm leading-relaxed text-dh-cream/80">
                  <span className="mt-2 h-1 w-1 flex-shrink-0 rounded-full bg-dh-gold" />
                  {b}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        <form onSubmit={submit} data-testid="arc-form" noValidate className="mt-4">
          <Section title="Reviewer Information">
            <div className="grid gap-7 sm:grid-cols-2">
              <div><label htmlFor="full_name" className={labelCls}>Full Name *</label><input id="full_name" data-testid="arc-full-name" className={inputCls} value={f.full_name} onChange={set("full_name")} placeholder="Your full name" /></div>
              <div><label htmlFor="email" className={labelCls}>Email Address *</label><input id="email" data-testid="arc-email" type="email" className={inputCls} value={f.email} onChange={set("email")} placeholder="you@email.com" /></div>
              <div><label htmlFor="preferred_name" className={labelCls}>Preferred / Display Name</label><input id="preferred_name" className={inputCls} value={f.preferred_name} onChange={set("preferred_name")} placeholder="Optional" /></div>
              <div><label htmlFor="social_handles" className={labelCls}>Primary social / review handle(s)</label><input id="social_handles" className={inputCls} value={f.social_handles} onChange={set("social_handles")} placeholder="@yourhandle" /></div>
              <div><label htmlFor="city" className={labelCls}>City</label><input id="city" className={inputCls} value={f.city} onChange={set("city")} /></div>
              <div><label htmlFor="state" className={labelCls}>State</label><input id="state" className={inputCls} value={f.state} onChange={set("state")} /></div>
            </div>
            <div>
              <label className={labelCls}>Age Confirmation *</label>
              <Radio name="age" options={["I am 18 or older", "I am under 18"]} value={f.age_confirmation} onChange={(v) => setF((s) => ({ ...s, age_confirmation: v }))} />
              {f.age_confirmation === "I am under 18" && (
                <p data-testid="arc-age-ineligible" className="mt-4 border border-dh-gold/30 bg-dh-forest/15 p-4 font-body text-sm leading-relaxed text-dh-cream/85">
                  Thank you for your interest in the Dhamma ARC Team. At this time, ARC applicants must be 18 years of age or older.
                </p>
              )}
            </div>
          </Section>

          <Section title="Reading & Review Platforms">
            <div>
              <label className={labelCls}>Where do you currently post book content or reviews? Check all that apply.</label>
              <CheckGroup options={PLATFORMS} values={f.review_platforms} onToggle={(v) => toggle("review_platforms", v)} testPrefix="arc-platform" />
            </div>
            <div><label htmlFor="profile_links" className={labelCls}>Profile links or usernames</label><textarea id="profile_links" rows={3} className={`${inputCls} resize-none`} value={f.profile_links} onChange={set("profile_links")} /></div>
            <div className="grid gap-7 sm:grid-cols-2">
              <div><label htmlFor="follower_count" className={labelCls}>Approx. follower count (primary platform)</label><input id="follower_count" className={inputCls} value={f.follower_count} onChange={set("follower_count")} /></div>
            </div>
            <div><label className={labelCls}>Book-content posting frequency</label><Radio name="freq" options={FREQ} value={f.posting_frequency} onChange={(v) => setF((s) => ({ ...s, posting_frequency: v }))} /></div>
          </Section>

          <Section title="Reader Fit">
            <div>
              <label className={labelCls}>Which genres do you actively read? Check all that apply.</label>
              <CheckGroup options={GENRES} values={f.genres} onToggle={(v) => toggle("genres", v)} testPrefix="arc-genre" />
            </div>
            <div><label htmlFor="recent_books_authors" className={labelCls}>Name 2–3 books or authors you have recently enjoyed</label><input id="recent_books_authors" className={inputCls} value={f.recent_books_authors} onChange={set("recent_books_authors")} /></div>
            <div><label htmlFor="interest_reason" className={labelCls}>Why are you interested in reading {titleRevealed ? <em>{firstBook.title}</em> : bookRef}?</label><textarea id="interest_reason" rows={4} className={`${inputCls} resize-none`} value={f.interest_reason} onChange={set("interest_reason")} /></div>
            <div><label className={labelCls}>Reviewing style</label><Radio name="style" options={STYLES} value={f.reviewing_style} onChange={(v) => setF((s) => ({ ...s, reviewing_style: v }))} /></div>
          </Section>

          <Section title="ARC Preferences & Availability">
            <div>
              <label className={labelCls}>Preferred ARC format</label>
              <Radio name="format" options={["Digital PDF", "Print", "Either"]} value={f.preferred_arc_format} onChange={(v) => setF((s) => ({ ...s, preferred_arc_format: v }))} />
              <p data-testid="arc-print-note" className="mt-3 font-body text-xs leading-relaxed text-dh-muted">
                Print ARCs are limited and not guaranteed. Selecting print does not guarantee a physical copy.
              </p>
            </div>
            <div><label className={labelCls}>Willing to finish by requested review window?</label><Radio name="window" options={["Yes", "No", "It depends on the deadline"]} value={f.review_window_commitment} onChange={(v) => setF((s) => ({ ...s, review_window_commitment: v }))} /></div>
            <div><label className={labelCls}>Comfortable avoiding major spoilers before release?</label><Radio name="spoiler" options={["Yes", "No"]} value={f.spoiler_agreement} onChange={(v) => setF((s) => ({ ...s, spoiler_agreement: v }))} /></div>
            <div><label className={labelCls}>Willing to clearly disclose receipt of a free ARC?</label><Radio name="disclose" options={["Yes", "No"]} value={f.disclosure_agreement} onChange={(v) => setF((s) => ({ ...s, disclosure_agreement: v }))} /></div>
            <div><label htmlFor="accessibility_needs" className={labelCls}>Accessibility or reading-format needs (optional)</label><textarea id="accessibility_needs" rows={2} className={`${inputCls} resize-none`} value={f.accessibility_needs} onChange={set("accessibility_needs")} /></div>
          </Section>

          <Section title="ARC Conduct & Security">
            <div className="space-y-3">
              {CONDUCT.map((c) => (
                <label key={c} data-testid={`arc-conduct-${CONDUCT.indexOf(c)}`} className="flex cursor-pointer items-start gap-3 border border-dh-cream/10 bg-dh-bg2/40 p-4 transition-colors hover:border-dh-gold/40">
                  <input type="checkbox" checked={f.conduct_acknowledgments.includes(c)} onChange={() => toggle("conduct_acknowledgments", c)} className="mt-1 h-4 w-4 accent-dh-gold" />
                  <span className="font-body text-sm leading-relaxed text-dh-cream/85">{c}</span>
                </label>
              ))}
            </div>
          </Section>

          <Section title="Additional Questions">
            <div><label htmlFor="prior_arc_experience" className={labelCls}>Have you previously received ARCs from authors or publishers? If yes, briefly describe.</label><textarea id="prior_arc_experience" rows={3} className={`${inputCls} resize-none`} value={f.prior_arc_experience} onChange={set("prior_arc_experience")} /></div>
            <div><label htmlFor="additional_notes" className={labelCls}>Is there anything else you would like Dhamma Publishing House to know?</label><textarea id="additional_notes" rows={3} className={`${inputCls} resize-none`} value={f.additional_notes} onChange={set("additional_notes")} /></div>
          </Section>

          <label data-testid="arc-certification" className="mt-12 flex cursor-pointer items-start gap-3 border border-dh-gold/30 bg-dh-forest/15 p-5">
            <input type="checkbox" checked={f.certification_accepted} onChange={(e) => setF((s) => ({ ...s, certification_accepted: e.target.checked }))} className="mt-1 h-4 w-4 accent-dh-gold" />
            <span className="font-body text-sm leading-relaxed text-dh-cream/85">
              By submitting this application, I certify that the information provided is accurate. If selected, I
              understand that I will be asked to review and accept the Dhamma ARC Reviewer Agreement before receiving
              my ARC copy.
            </span>
          </label>

          <p className="mt-10 text-center font-body text-sm text-dh-cream/60">
            Questions about the ARC program?{" "}
            <a data-testid="arc-contact-email" href={`mailto:${ARC_CONTACT_EMAIL}`} className="text-dh-gold underline-offset-4 transition-colors hover:underline">{ARC_CONTACT_EMAIL}</a>
          </p>

          <button type="submit" data-testid="arc-submit" disabled={loading || f.age_confirmation === "I am under 18"} className="group mt-10 inline-flex items-center justify-center gap-3 border border-dh-gold/70 bg-transparent px-10 py-4 font-body text-[11px] uppercase tracking-[0.22em] text-dh-gold transition-[background-color,color] duration-500 hover:bg-dh-gold hover:text-dh-bg2 disabled:opacity-50">
            {loading ? "Submitting…" : "Submit Application"}
            <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1" />
          </button>
        </form>
      </div>
    </section>
  );
}
